import { useState, useEffect } from 'react';
import { getColorPresets, type ColorPreset } from '@/lib/api';
import { LayerColors } from '@/types/canvas';
import { cn } from '@/lib/utils';

/** Vestavěné presety při selhání API nebo prázdné DB (stejná data jako seed 008) – export pro ColorLayerPicker */
export const FALLBACK_COLOR_PRESETS: ColorPreset[] = [
  { id: 'fallback-default', name: 'Default', colors: { background: '#F5F5F5', water: '#AAD3DF', land: '#FFFFFF', park: '#C8E6C9', forest: '#A5D6A7', building: '#E0E0E0', building_outline: '#BDBDBD', road_motorway: '#FFB74D', road_major: '#FFFFFF', road_minor: '#F5F5F5', road_path: '#E0E0E0', label_text: '#333333', label_halo: '#FFFFFF' }, is_public: true, is_default: false, sort_order: 0, tags: ['default', 'colorScheme'], created_at: '', updated_at: '' },
  { id: 'fallback-monochrome', name: 'Monochrome', colors: { background: '#FFFFFF', water: '#E8E8E8', land: '#FFFFFF', park: '#F5F5F5', forest: '#EEEEEE', building: '#E0E0E0', building_outline: '#D0D0D0', road_motorway: '#666666', road_major: '#999999', road_minor: '#CCCCCC', road_path: '#E0E0E0', label_text: '#000000', label_halo: '#FFFFFF' }, is_public: true, is_default: false, sort_order: 1, tags: ['monochrome', 'colorScheme'], created_at: '', updated_at: '' },
  { id: 'fallback-nautic', name: 'Nautic', colors: { background: '#E8F4F8', water: '#4A90E2', land: '#F5E6D3', park: '#A8D5BA', forest: '#7CB685', building: '#D4C5B9', building_outline: '#B8A89C', road_motorway: '#8B7355', road_major: '#FFFFFF', road_minor: '#F0E4D7', road_path: '#E0D4C7', label_text: '#2C3E50', label_halo: '#FFFFFF' }, is_public: true, is_default: false, sort_order: 2, tags: ['nautic', 'colorScheme'], created_at: '', updated_at: '' },
  { id: 'fallback-blue-white', name: 'Blue-White', colors: { background: '#F0F8FF', water: '#B3D9FF', land: '#FFFFFF', park: '#E3F2FD', forest: '#BBDEFB', building: '#E1F5FE', building_outline: '#B3E5FC', road_motorway: '#90CAF9', road_major: '#FFFFFF', road_minor: '#F5F5F5', road_path: '#E3F2FD', label_text: '#1976D2', label_halo: '#FFFFFF' }, is_public: true, is_default: false, sort_order: 3, tags: ['blue-white', 'colorScheme'], created_at: '', updated_at: '' },
  { id: 'fallback-vintage', name: 'Vintage', colors: { background: '#F5F0E8', water: '#B8A898', land: '#F5EBD8', park: '#D4C5A9', forest: '#B8A888', building: '#E0D5C5', building_outline: '#C8BDA8', road_motorway: '#A08870', road_major: '#E8DCC8', road_minor: '#D8CDB8', road_path: '#C8BDA8', label_text: '#5D4E37', label_halo: '#F5EBD8' }, is_public: true, is_default: false, sort_order: 4, tags: ['vintage', 'colorScheme'], created_at: '', updated_at: '' },
];

interface ColorPresetsButtonsProps {
  selectedPresetId?: string;
  onPresetSelect: (colors: LayerColors, presetId?: string) => void;
}

export function ColorPresetsButtons({ selectedPresetId, onPresetSelect }: ColorPresetsButtonsProps) {
  const [presets, setPresets] = useState<ColorPreset[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [usedFallback, setUsedFallback] = useState(false);

  useEffect(() => {
    loadPresets();
  }, []);

  const loadPresets = async () => {
    setIsLoading(true);
    setUsedFallback(false);
    try {
      const result = await getColorPresets(false); // Pouze veřejné presety
      if (result.success && result.data) {
        const presetsArray = Array.isArray(result.data) ? result.data : [];
        const colorSchemePresets = presetsArray.filter((p: ColorPreset) =>
          p.tags?.includes('colorScheme') || p.is_public
        );
        const sortedPresets = colorSchemePresets.sort((a: ColorPreset, b: ColorPreset) => (a.sort_order || 0) - (b.sort_order || 0));
        if (sortedPresets.length > 0) {
          setPresets(sortedPresets);
          return;
        }
      }
      setPresets(FALLBACK_COLOR_PRESETS);
      setUsedFallback(true);
    } catch (error) {
      console.error('[ColorPresetsButtons] Chyba při načítání presetů:', error);
      setPresets(FALLBACK_COLOR_PRESETS);
      setUsedFallback(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePresetSelect = (preset: ColorPreset) => {
    onPresetSelect(preset.colors, preset.id);
  };

  if (isLoading) {
    return (
      <div className="mt-4">
        <div className="flex gap-2 pb-2">
          <div className="px-4 py-2 text-sm text-boho-cream/90">Načítání...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-4">
      {usedFallback && (
        <p className="text-xs text-boho-cream/80 mb-2">
          Presety načteny z výchozích hodnot (worker/API nedostupné – zkontrolujte, že běží na http://127.0.0.1:8787).
        </p>
      )}
      <div className="flex gap-2 overflow-x-auto lg:overflow-x-visible lg:flex-wrap pb-2 scrollbar-hide">
        {presets.map((preset) => (
          <button
            key={preset.id}
            onClick={() => handlePresetSelect(preset)}
            className={cn(
              'flex-shrink-0 px-4 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap',
              selectedPresetId === preset.id
                ? 'bg-posterden-red text-white shadow-lg shadow-posterden-red/20'
                : 'text-boho-cream bg-white/10 border border-white/20 hover:bg-white/15 hover:border-white/30'
            )}
          >
            {preset.name}
          </button>
        ))}
      </div>
    </div>
  );
}
